import { useEffect, useRef } from 'react';
import { Pencil, Trash2 } from 'lucide-react';

interface TaskContextMenuProps {
  x: number;
  y: number;
  onEdit: () => void;
  onDelete: () => void;
  onClose: () => void;
}

/**
 * Simple floating context menu for timeline task cards.
 *
 * Clicking outside the menu or pressing Escape closes it. Only one action
 * is exposed for now (Delete) matching the issue #17 scope.
 */
export function TaskContextMenu({ x, y, onEdit, onDelete, onClose }: TaskContextMenuProps) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(event: MouseEvent) {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        onClose();
      }
    }

    function handleKey(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        onClose();
      }
    }

    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [onClose]);

  return (
    <div
      ref={ref}
      className="fixed z-50 min-w-[160px] rounded-md border bg-popover p-1 text-popover-foreground shadow-md"
      style={{ left: x, top: y }}
      role="menu"
    >
      <button
        type="button"
        onClick={() => {
          onEdit();
          onClose();
        }}
        className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent"
        role="menuitem"
      >
        <Pencil className="h-4 w-4" />
        Edit task
      </button>
      <button
        type="button"
        onClick={() => {
          onDelete();
          onClose();
        }}
        className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm text-destructive outline-none hover:bg-destructive/10"
        role="menuitem"
      >
        <Trash2 className="h-4 w-4" />
        Delete task
      </button>
    </div>
  );
}
